import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AbstractCrudService } from '../../../shared/components/crud/abstract-crud.service';
import { API_PATH, IBGE_API_PATH } from 'src/environments/environment';
import { RELATIVE_URL } from './crud-config';
import { ICustomer } from 'src/app/domain/customer/ICustomer';

@Injectable({
  providedIn: 'root',
})
export class CustomerService extends AbstractCrudService {
  override title: string = 'Cliente';
  override pluralTitle = 'Clientes';

  static isAdvancedFilterActive = false;
  static lastAppliedFilter = null;
  static params = { limit: 25, page: 0, filter: '' };
  static isNavigatingToNew = false;

  constructor(protected override httpClient: HttpClient) {
    super(httpClient, RELATIVE_URL);
  }

  public findAll(params?: any): Observable<ICustomer[]> {
    const queryParams = params || { limit: 100, page: 0 };
    return this.httpClient.get<ICustomer[]>(`${API_PATH}${this.relativeUrl}`, {
      params: queryParams,
    });
  }

  public getCustomersByUser(userId: string): Observable<ICustomer[]> {
    return this.httpClient.get<ICustomer[]>(
      `${API_PATH}${this.relativeUrl}/byUser/${userId}`
    );
  }

  public findByName(name: string): Observable<Object> {
    return this.httpClient.get<Object>(
      `${API_PATH}${this.relativeUrl}/byName/${name}`
    );
  }

  public getCnpjInfo(cnpj: string): Observable<ICustomer> {
    return this.httpClient.get<ICustomer>(
      `${API_PATH}${RELATIVE_URL}/cnpj/${cnpj}`
    );
  }

  public getCitiesByUf(uf: string): Observable<any> {
    return this.httpClient.get(
      `${IBGE_API_PATH}localidades/estados/${uf}/municipios`
    );
  }

  public getStates(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${IBGE_API_PATH}localidades/estados`);
  }

  public getCepInfo(cep: string): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}${RELATIVE_URL}/cep/${cep}`);
  }

  public findByFilter(filterForm: any, pagination: any): Observable<any[]> {
    let params = {
      ...{ getRelation: 0, limit: 36, page: 0, filter: '' },
      ...pagination,
    };

    return this.httpClient.post<any[]>(
      `${API_PATH}${this.relativeUrl}/filter`,
      { ...filterForm, ...params }
    );
  }

  public changeActiveStatus(userId: string, isActive: boolean) {
    return this.httpClient.put(
      `${API_PATH}${this.relativeUrl}/change-active/${userId}`,
      { isActive }
    );
  }

  public changeLockStatus(userId: string, isLock: boolean) {
    return this.httpClient.put(
      `${API_PATH}${this.relativeUrl}/change-lock/${userId}`,
      { isLock }
    );
  }

  public getCustomerCounts(): Observable<{
    total: number;
    ativos: number;
    inativos: number;
    bloqueados: number;
  }> {
    return this.httpClient.get<{
      total: number;
      ativos: number;
      inativos: number;
      bloqueados: number;
    }>(`${API_PATH}customers/counts`);
  }

  public getCustomersByStatus(
    status: 'all' | 'active' | 'inactive' | 'blocked'
  ): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${API_PATH}customers/by-status/${status}`
    );
  }

  public getCustomerByPartner(): Observable<
    { name: string; value: number; id?: string }[]
  > {
    return this.httpClient.get<{ name: string; value: number; id?: string }[]>(
      `${API_PATH}${this.relativeUrl}/customer-by-partner`
    );
  }

  public getGrowthCustomers(): Observable<{ name: string; value: number }[]> {
    return this.httpClient.get<{ name: string; value: number }[]>(
      `${API_PATH}customers/crescimento-clientes`
    );
  }

  public getCustomerMonthlyTotal(partnerId?: string): Observable<{
    totalGeral: number;
    totalComissaoGeral: number;
    totalComissaoParceiro: number;
    totalComissaoConsultor: number;
    detalhes: any;
  }> {
    let url = `${API_PATH}${this.relativeUrl}/monthly-total`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;

    return this.httpClient.get<{
      totalGeral: number;
      totalComissaoGeral: number;
      totalComissaoParceiro: number;
      totalComissaoConsultor: number;
      detalhes: any;
    }>(url, { params });
  }

  public getCurrentMonthBirthdays(): Observable<{
    count: number;
    list: {
      id: string;
      name: string;
      birthday: string;
      type: string;
      cellPhone: string;
    }[];
  }> {
    return this.httpClient.get<{
      count: number;
      list: {
        id: string;
        name: string;
        birthday: string;
        type: string;
        cellPhone: string;
      }[];
    }>(`${API_PATH}${this.relativeUrl}/birthdays`);
  }

  public getAllCustomersWithBirthdays(): Observable<{
    count: number;
    list: {
      id: string;
      name: string;
      birthday: string;
      type: string;
      cellPhone: string;
      email: string;
      phone: string;
    }[];
  }> {
    return this.httpClient.get<{
      count: number;
      list: {
        id: string;
        name: string;
        birthday: string;
        type: string;
        cellPhone: string;
        email: string;
        phone: string;
      }[];
    }>(`${API_PATH}${this.relativeUrl}/all-with-birthdays`);
  }

  getContractsByCustomer(customerId: string): Observable<any[]> {
    return this.httpClient.get<any[]>(
      `${API_PATH}contracts/customer/${customerId}`
    );
  }

  /**
   * Verifica o status de assinatura dos contratos do cliente
   * @param customerId ID do cliente
   * @returns Observable que emite um objeto com informações sobre os contratos do cliente
   */
  getContractSignatureStatus(customerId: string): Observable<{
    hasContracts: boolean;
    allSigned: boolean;
    mixedStatus: boolean;
    signedCount: number;
    totalCount: number;
  }> {
    return this.getContractsByCustomer(customerId).pipe(
      map((contracts) => {
        const result = {
          hasContracts: contracts.length > 0,
          allSigned: false,
          mixedStatus: false,
          signedCount: 0,
          totalCount: contracts.length,
        };

        const signedContracts = contracts.filter((c) => c.status === 'signed');
        result.signedCount = signedContracts.length;

        if (result.hasContracts) {
          result.allSigned = result.signedCount === result.totalCount;
          result.mixedStatus =
            result.signedCount > 0 && result.signedCount < result.totalCount;
        }

        return result;
      })
    );
  }

  public updateDocumentPhoto(id: string, file?: File | null) {
    const url = `${API_PATH}${this.relativeUrl}/document-photo/${id}`;
    const form = new FormData();
    if (file) {
      form.append('documentPhoto', file, file.name || 'documentPhoto');
    }
    return this.httpClient.put(url, form);
  }

   public getById(id: string) {
    return this.httpClient.get(
      `${API_PATH}${this.relativeUrl}/${id}`,
    );
  }
}
